import {
  useMutation,
  UseMutationResult,
  UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import { InvitationWithRelationsDto, ResponseDto } from "@mimber/schema"
import { invitationsKeys } from "../invitations.utils"
import { RevokeInvitationParams } from "./useRevokeInvitation.types"

/**
 * Organization-specific hook for revoking a pending invitation
 * Revoked invitations cannot be accepted or resent
 * Can only be used in mimber-organization app
 * The organization context is automatically handled by ServerContext
 */
export const useRevokeInvitation = (
  options?: UseMutationOptions<
    ResponseDto<InvitationWithRelationsDto>,
    Error,
    RevokeInvitationParams
  >
): UseMutationResult<
  ResponseDto<InvitationWithRelationsDto>,
  Error,
  RevokeInvitationParams
> => {
  guardAppUsage("mimber-organization", "useRevokeInvitation")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async ({ uuid }: RevokeInvitationParams) => {
      const response = await server.delete<
        ResponseDto<InvitationWithRelationsDto>
      >(`invitations/${uuid}`)
      return response.data
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      // Invalidate the specific invitation query
      queryClient.invalidateQueries({
        queryKey: invitationsKeys.findOne(variables.uuid),
      })

      // Invalidate all invitation lists to show the revoked invitation
      queryClient.invalidateQueries({ queryKey: invitationsKeys.findAll() })

      // Call user's onSuccess if provided
      options?.onSuccess?.(data, variables, onMutateResult, context)
    },
  })
}
